import React, { useMemo, useState } from 'react';
import { ScrollView, StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { FontFamily } from '../../../constants/fonts';
import { SubmittersRepeatSubmitterData } from './types';
import SkeletonBlock from './SkeletonBlock';

interface TopRepeatSubmittersSectionProps {
  title: string;
  description: string;
  rows: SubmittersRepeatSubmitterData[];

  /**
   * When true the table body is replaced by shimmering placeholder rows that
   * mirror the real column layout, so the card keeps its size while loading.
   */
  loading?: boolean;

  /** Number of placeholder rows rendered while loading. */
  skeletonRowCount?: number;
  /** Optional error message to display inside the table when the request failed. */
  error?: string | null;
  /** Called when the user taps the Retry button shown in the error state. */
  onRetry?: () => void;
  /** When true, shows the filter-hint empty message instead of the generic one. */
  hasActiveFilters?: boolean;
}

/**
 * Placeholder rows shown while the table loads.
 *
 * Kept in this file so consumers get the skeleton just by passing `loading`,
 * and so it can reuse the exact column flex / minWidth styles defined below.
 */
const TableSkeleton: React.FC<{ rowCount: number }> = ({ rowCount }) => (
  <>
    {Array.from({ length: rowCount }).map((_, rowIndex) => (
      <View style={styles.row} key={`repeat-submitters-skeleton-${rowIndex}`}>
        <View style={[styles.skeletonCell, styles.skeletonUserCell]}>
          <SkeletonBlock style={styles.skeletonWide} />
        </View>

        {Array.from({ length: 5 }).map((__, numIndex) => (
          <View
            key={`repeat-submitters-skeleton-${rowIndex}-num-${numIndex}`}
            style={[styles.skeletonCell, styles.skeletonNumCell]}
          >
            <SkeletonBlock style={styles.skeletonNarrow} />
          </View>
        ))}

        <View style={[styles.skeletonCell, styles.skeletonTopCategoryCell]}>
          <SkeletonBlock style={styles.skeletonWide} />
        </View>
        <View style={[styles.skeletonCell, styles.skeletonRoleCell]}>
          <SkeletonBlock style={styles.skeletonMedium} />
        </View>
        <View style={[styles.skeletonCell, styles.skeletonLocationCell]}>
          <SkeletonBlock style={styles.skeletonMedium} />
        </View>
      </View>
    ))}
  </>
);

const TopRepeatSubmittersSection: React.FC<TopRepeatSubmittersSectionProps> = ({
  title,
  description,
  rows,
  loading = false,
  skeletonRowCount = 5,
  error = null,
  onRetry,
  hasActiveFilters = false,
}) => {
  
  const [sortKey, setSortKey] = useState<'user' | 'ticketsSubmitted' | 'ticketsClosed' | 'ticketsOpen' | 'resolutionRate' | 'averageResolutionTime' | 'topCategory' | 'navigatorRole' | 'location'>('ticketsSubmitted');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [searchText, setSearchText] = useState('');

  const handleSort = (key: typeof sortKey) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortKey(key);
    setSortDirection(key === 'user' || key === 'topCategory' || key === 'navigatorRole' || key === 'location' ? 'asc' : 'desc');
  };

  const filteredRows = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) {
      return rows;
    }

    return rows.filter((item) => {
      return (
        item.user.toLowerCase().includes(query) ||
        item.topCategoryName.toLowerCase().includes(query) ||
        item.navigatorRole.toLowerCase().includes(query)
      );
    });
  }, [rows, searchText]);

  const sortedRows = useMemo(() => {
    const directionMultiplier = sortDirection === 'asc' ? 1 : -1;
    const getComparable = (item: SubmittersRepeatSubmitterData): string | number => {
      if (sortKey === 'topCategory') {
        return item.topCategoryName;
      }

      return item[sortKey];
    };

    return [...filteredRows].sort((a, b) => {
      const left = getComparable(a);
      const right = getComparable(b);

      if (typeof left === 'number' && typeof right === 'number') {
        return (left - right) * directionMultiplier;
      }

      return String(left).localeCompare(String(right)) * directionMultiplier;
    });
  }, [filteredRows, sortDirection, sortKey]);

  const renderHeader = (label: string, key: typeof sortKey, style: any) => (
    <TouchableOpacity onPress={() => handleSort(key)} style={[styles.headerCellWrap, style]}>
      <Text style={[styles.headerCell, key === 'user' || key === 'topCategory' || key === 'navigatorRole' || key === 'location' ? styles.textLeft : styles.textRight]} numberOfLines={1}>
        {label}
        {sortKey === key ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : ''}
      </Text>
    </TouchableOpacity>
  );

  const extractUserName = (email: string) => {
    if (!email) return 'N/A';
    let name = email.split('@')[0];
    if (name.endsWith('.Contractor')) {
      name = name.replace('.Contractor', '');
    }
    return name;
  };

  return (
    <View style={styles.card}>
      <View style={styles.cardHead}>
        <View style={styles.cardHeadLeft}>
          <Text style={styles.cardTitle}>{title}</Text>
          {loading && !description ? (
            <SkeletonBlock style={styles.skeletonNote} />
          ) : (
            <Text style={styles.cardNote}>{description}</Text>
          )}
        </View>

        <View style={styles.searchWrap}>
          <Svg width={15} height={15} viewBox="0 0 24 24" style={styles.searchIcon}>
            <Path
              d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"
              fill="#818181"
            />
          </Svg>
          <TextInput
            value={searchText}
            onChangeText={setSearchText}
            placeholder="Search user, category, or role"
            placeholderTextColor="#8A8E94"
            style={styles.searchInput}
          />
        </View>
      </View>

        {loading ? (
          <ScrollView horizontal showsHorizontalScrollIndicator={true} contentContainerStyle={{ minWidth: '100%' }}>
            <View style={[styles.table, { minWidth: '100%' }]}> 
              <View style={[styles.row, styles.headerRow]}>
                {renderHeader('User', 'user', styles.userCell)}
                {renderHeader('Tickets Submitted', 'ticketsSubmitted', styles.numCell)}
                {renderHeader('Tickets Closed', 'ticketsClosed', styles.numCell)}
                {renderHeader('Tickets Open', 'ticketsOpen', styles.numCell)}
                {renderHeader('Resolution Rate', 'resolutionRate', styles.numCell)}
                {renderHeader('Average Resolution Time', 'averageResolutionTime', styles.numCell)}
                {renderHeader('Top Category', 'topCategory', styles.topCategoryCell)}
                {renderHeader('Navigator Role', 'navigatorRole', styles.roleCell)}
                {renderHeader('Location', 'location', styles.locationCell)}
              </View>

              <TableSkeleton rowCount={skeletonRowCount} />
            </View>
          </ScrollView>
        ) : sortedRows.length ? (
          <ScrollView horizontal showsHorizontalScrollIndicator={true} contentContainerStyle={{ minWidth: '100%' }}>
            <View style={[styles.table, { minWidth: '100%' }]}> 
              <View style={[styles.row, styles.headerRow]}>
                {renderHeader('User', 'user', styles.userCell)}
                {renderHeader('Tickets Submitted', 'ticketsSubmitted', styles.numCell)}
                {renderHeader('Tickets Closed', 'ticketsClosed', styles.numCell)}
                {renderHeader('Tickets Open', 'ticketsOpen', styles.numCell)}
                {renderHeader('Resolution Rate', 'resolutionRate', styles.numCell)}
                {renderHeader('Average Resolution Time', 'averageResolutionTime', styles.numCell)}
                {renderHeader('Top Category', 'topCategory', styles.topCategoryCell)}
                {renderHeader('Navigator Role', 'navigatorRole', styles.roleCell)}
                {renderHeader('Location', 'location', styles.locationCell)}
              </View>

              {(loading ? [] : sortedRows).map((item) => (
                <View style={styles.row} key={`${item.user}-${item.location}`}>
                  <Text style={[styles.cell, styles.userCell, styles.link, styles.textLeft]} numberOfLines={1}>{extractUserName(item.user)}</Text>
                  <Text style={[styles.cell, styles.numCell, styles.textRight]}>{item.ticketsSubmitted}</Text>
                  <Text style={[styles.cell, styles.numCell, styles.textRight]}>{item.ticketsClosed}</Text>
                  <Text style={[styles.cell, styles.numCell, styles.textRight]}>{item.ticketsOpen}</Text>
                  <Text style={[styles.cell, styles.numCell, styles.textRight]}>{item.resolutionRate.toFixed(1)}%</Text>
                  <Text style={[styles.cell, styles.numCell, styles.textRight]}>{item.averageResolutionTime.toFixed(1)} hrs</Text>
                  <Text style={[styles.cell, styles.topCategoryCell, styles.textLeft]}>{`${item.topCategoryName} (${item.topCategoryCount})`}</Text>
                  <Text style={[styles.cell, styles.roleCell, styles.textLeft]} numberOfLines={1}>{item.navigatorRole}</Text>
                  <Text style={[styles.cell, styles.locationCell, styles.link, styles.textLeft]} numberOfLines={1}>{item.location}</Text>
                </View>
              ))}
            </View>
          </ScrollView>
      ) : error ? (
        <View style={styles.emptyWrap}>
          <Text style={styles.errorText}>{error}</Text>
          {!!onRetry && (
            <TouchableOpacity onPress={onRetry} style={styles.retryButton} accessibilityRole="button" accessibilityLabel="Retry">
              <Text style={styles.retryText}>↻  Retry</Text>
            </TouchableOpacity>
          )}
        </View>
      ) : (
        <View style={styles.emptyWrap}>
          <Text style={styles.noResults}>{searchText ? `No results found for "${searchText}"` : 'Try expanding your date range or removing some filters.'}</Text>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E8EDF3',
    padding: 20,
    marginBottom: 16,
  },
  cardHead: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    marginBottom: 12,
    rowGap: 10,
    columnGap: 12,
  },
  cardHeadLeft: {
    gap: 4,
    flexShrink: 1,
  },
  cardTitle: {
    fontSize: 16,
    fontFamily: FontFamily.bold,
    color: '#2D2F33',
  },
  cardNote: {
    fontSize: 12,
    color: '#7A8088',
  },
  searchWrap: {
    width: 260,
    maxWidth: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderWidth: 1,
    borderColor: '#D0D5DD',
    borderRadius: 6,
    paddingHorizontal: 12,
    height: 32,
    backgroundColor: '#FFFFFF',
  },
  searchIcon: {
    flexShrink: 0,
  },
  searchInput: {
    fontSize: 13,
    fontFamily: FontFamily.regular,
    color: '#2D2F33',
    paddingVertical: 0,
    flex: 1,
  },
  table: {
    flex: 1,
    minWidth: '100%',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#EEF2F6',
    minHeight: 48,
  },
  headerRow: {
    borderBottomColor: '#DCE3EC',
    borderBottomWidth: 2,
    minHeight: 56,
  },
  cell: {
    fontSize: 13,
    fontFamily: FontFamily.regular,
    color: '#2D2F33',
    paddingVertical: 12,
    paddingHorizontal: 12,
    textAlignVertical: 'center',
    includeFontPadding: false,
  },
  headerCellWrap: {
    paddingVertical: 16,
    paddingHorizontal: 12,
    justifyContent: 'center',
  },
  headerCell: {
    fontSize: 11,
    color: '#6A6D71',
    fontFamily: FontFamily.bold,
    textTransform: 'uppercase',
    letterSpacing: 0.2,
    includeFontPadding: false,
  },
  userCell: {
    flex: 1,
    minWidth: 130,
  },
  numCell: {
    flex: 1.1,
    minWidth: 220,
    textAlign: 'right',
  },
  topCategoryCell: {
    flex: 1.3,
    minWidth: 150,
  },
  roleCell: {
    flex: 1.1,
    minWidth: 130,
  },
  locationCell: {
    flex: 1,
    minWidth: 120,
  },
  link: {
    color: '#005CBC',
    fontFamily: FontFamily.bold,
  },
  emptyWrap: {
    minHeight: 160,
    justifyContent: 'center',
    alignItems: 'center',
  },
  noResults: {
    fontSize: 14,
    color: '#7A8088',
    fontFamily: FontFamily.medium,
    textAlign: 'center',
  },
  errorText: {
    fontSize: 14,
    color: '#B42318',
    fontFamily: FontFamily.medium,
    textAlign: 'center',
  },
  retryButton: {
    marginTop: 12,
    paddingVertical: 6,
    paddingHorizontal: 16,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#1D4ED8',
    alignSelf: 'center',
  },
  retryText: {
    color: '#1D4ED8',
    fontSize: 13,
    fontFamily: FontFamily.medium,
    fontWeight: '600',
  },
  textLeft: {
    textAlign: 'left',
  },
  textRight: {
    textAlign: 'right',
  },

  // ─── Loading skeleton ────────────────────────────────────────────────────
  // The `*Cell` styles above carry `textAlign`, which is invalid on a View, so
  // the skeleton duplicates only flex/minWidth and maps the text alignment to
  // `alignItems` so placeholder bars sit where the real text does.
  skeletonCell: {
    paddingVertical: 12,
    paddingHorizontal: 12,
    justifyContent: 'center',
  },
  skeletonUserCell: {
    flex: 1,
    minWidth: 130,
    alignItems: 'flex-start',
  },
  skeletonNumCell: {
    flex: 1.1,
    minWidth: 220,
    alignItems: 'flex-end',
  },
  skeletonTopCategoryCell: {
    flex: 1.3,
    minWidth: 150,
    alignItems: 'flex-start',
  },
  skeletonRoleCell: {
    flex: 1.1,
    minWidth: 130,
    alignItems: 'flex-start',
  },
  skeletonLocationCell: {
    flex: 1,
    minWidth: 120,
    alignItems: 'flex-start',
  },
  skeletonWide: {
    width: '80%',
    height: 12,
  },
  skeletonMedium: {
    width: '65%',
    height: 12,
  },
  skeletonNarrow: {
    width: 36,
    height: 12,
  },
  skeletonNote: {
    width: 260,
    height: 10,
  },
});

export default TopRepeatSubmittersSection;






